import { TEAMS_LINK, TeamsLogo, WHATSAPP_NUMBER, WhatsAppLogo } from "@/lib";
import { motion, Variants } from "framer-motion";
import { Sparkles, ChevronDown } from "lucide-react"; 
import React from "react";

const VIDEO_PLACEHOLDER_URL = "/3163534-sd_960_540_30fps.mp4";
// Use a real image path here, for example:
const VIDEO_POSTER_IMAGE_URL = "/13636.jpg"; 

export default function HeroSection() {
    // CSS styles for the video background and the new loading animation
    const VideoBackgroundStyle = () => (
        <style>{`
        .video-background-container {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 0;
        }

        .video-background {
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0.25; 
          filter: brightness(0.7) blur(1px);
        }

        /* --- New CSS for Loading Animation --- */
        .video-background[poster][data-loading="true"] {
            position: relative;
        }

        .video-background[poster][data-loading="true"]::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(30, 41, 59, 0.7); /* Darker overlay */
            pointer-events: none;
            z-index: 10;
        }

        .video-background[poster][data-loading="true"]::after {
            content: 'Loading Expert Systems...';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            color: #93c5fd; /* Light blue text */
            font-size: 1.25rem;
            font-weight: 600;
            z-index: 20;
            animation: pulse-glow 2s infinite ease-in-out;
            text-shadow: 0 0 5px rgba(147, 197, 253, 0.5); /* Subtle glow */
        }

        @keyframes pulse-glow {
            0% { opacity: 0.8; transform: translate(-50%, -50%) scale(1); }
            50% { opacity: 1; transform: translate(-50%, -50%) scale(1.05); }
            100% { opacity: 0.8; transform: translate(-50%, -50%) scale(1); }
        }
        /* ------------------------------------- */
      `}</style>
    );
    
    // State to track if the video is ready to play (loaded)
    const [videoLoaded, setVideoLoaded] = React.useState(false); 

    // Framer Motion variants (unchanged)
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.15 },
        },
    };

    const textVariants: Variants = {
        hidden: { y: 30, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { type: 'spring', stiffness: 70, duration: 0.8 },
        },
    };

    const buttonVariants: Variants = {
        hidden: { y: 50, opacity: 0, scale: 0.8 },
        visible: {
            y: 0,
            opacity: 1,
            scale: 1,
            transition: { type: 'spring', stiffness: 100, damping: 15, duration: 0.6 },
        },
    };

    return (
        <section id="hero" className="relative min-h-screen flex items-center justify-center bg-gray-800 py-24 px-4 sm:px-6 lg:px-8 text-center overflow-hidden">
            <VideoBackgroundStyle />
            
            {/* The video container is now separate to handle the loading state */}
            <div className="video-background-container">
                <video 
                    className="video-background"
                    autoPlay 
                    loop 
                    muted 
                    playsInline 
                    // This attribute is crucial for tracking the load state
                    onLoadedData={() => setVideoLoaded(true)} 
                    // Set data-loading based on state. If false, CSS will apply the animation.
                    data-loading={!videoLoaded} 
                    poster={VIDEO_POSTER_IMAGE_URL} 
                >
                    <source src={VIDEO_PLACEHOLDER_URL} type="video/mp4" />
                </video>
            </div>

            <motion.div
                className="max-w-5xl z-10"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {/* Optional: Badge above title */}
                <motion.div variants={textVariants} className="flex justify-center mb-6">
                    <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-indigo-500/20 border border-indigo-500/30 text-indigo-200 text-sm font-medium backdrop-blur-sm">
                        <Sparkles className="w-4 h-4 mr-2 text-indigo-400" />
                        New: AI Architecture Consultant
                    </span>
                </motion.div>

                <motion.h1
                    className="text-4xl sm:text-5xl md:text-7xl font-extrabold tracking-tight"
                    variants={textVariants}
                >
                    <span className="block text-white">Mohan WorkSpace</span>
                    <span className="mt-2 block bg-gradient-to-r from-indigo-400 to-sky-300 bg-clip-text text-transparent">
                        Expert Tech Leadership
                    </span>
                </motion.h1>

                <motion.p
                    className="mt-6 text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed"
                    variants={textVariants}
                >
                    Seasoned technology leader with 12+ years of experience driving digital transformation, scaling engineering teams, and delivering robust, secure solutions for fintech & SaaS.
                </motion.p>

                <motion.div
                    className="mt-10 flex flex-col sm:flex-row justify-center gap-4"
                    variants={containerVariants}
                >
                    {/* 1. WhatsApp Button */}
                    <motion.a
                        href={`https://wa.me/${WHATSAPP_NUMBER}`}
                        target="_blank"
                        className="inline-flex items-center justify-center bg-green-600 hover:bg-green-500 text-white px-8 py-3.5 rounded-xl text-lg font-medium shadow-lg shadow-green-900/20 transition-all"
                        variants={buttonVariants}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <WhatsAppLogo className="w-6 h-6 mr-2" />
                        <span>Chat on WhatsApp</span>
                    </motion.a>

                    {/* 2. Teams Button */}
                    <motion.a
                        href={TEAMS_LINK}
                        target="_blank"
                        className="inline-flex items-center justify-center bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3.5 rounded-xl text-lg font-medium shadow-lg shadow-indigo-900/20 transition-all"
                        variants={buttonVariants}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <TeamsLogo className="w-6 h-6 mr-2" />
                        <span>Join Community</span>
                    </motion.a>

                    {/* 3. NEW BUTTON: Links to #analyzer */}
                    <motion.a
                        href="#analyzer"
                        className="inline-flex items-center justify-center bg-gray-800/80 hover:bg-gray-700 text-indigo-300 border border-indigo-500/30 px-8 py-3.5 rounded-xl text-lg font-medium backdrop-blur-md transition-all group"
                        variants={buttonVariants}
                        whileHover={{ scale: 1.05, backgroundColor: "rgba(31, 41, 55, 0.9)" }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <Sparkles className="w-5 h-5 mr-2 text-indigo-400 group-hover:text-white transition-colors" />
                        <span>Get Strategic Assessment</span>
                    </motion.a>
                </motion.div>
            </motion.div>

            {/* Optional: Scroll Down Indicator */}
            <motion.a 
                href="#analyzer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, y: [0, 10, 0] }}
                transition={{ delay: 2, duration: 2, repeat: Infinity }}
                className="absolute bottom-10 text-gray-500 hover:text-white transition-colors cursor-pointer"
            >
                <ChevronDown className="w-8 h-8" />
            </motion.a>
        </section>
    )
}